<script setup lang="ts">
import { Editor, NodeViewProps, NodeViewWrapper } from "@tiptap/vue-3";
import katex from "katex";

import FormulaModal from "./FormulaModal.vue";

const props = defineProps<{
  editor: typeof Editor;
  node: typeof NodeViewProps;
  selected: boolean;
  updateAttributes: (typeof NodeViewProps)["updateAttributes"];
  deleteNode: (typeof NodeViewProps)["deleteNode"];
}>();

const rawFormula = ref(props.node.attrs.content);
const editMode = ref(false);

const options: katex.KatexOptions = {
  throwOnError: false,
  strict: false,
  displayMode: true,
  maxSize: 300,
  output: "mathml",
};

const renderedFormula = computed(() => {
  if (!props.node.attrs.content) {
    return "";
  }

  return katex.renderToString(props.node.attrs.content, options);
});

const enterEditMode = () => {
  editMode.value = true;
  rawFormula.value = props.node.attrs.content;
};

const submit = (content: string) => {
  props.updateAttributes({
    content: content,
  });
  editMode.value = false;
};

const handleHide = () => {
  editMode.value = false;
};
</script>
<template>
  <node-view-wrapper class="katex-wrapper" :class="{ selected: selected }">
    <FormulaModal
      :open="editMode"
      :content="rawFormula"
      @submit="submit"
      @hide="handleHide"
    />
    <button
      v-safe-html="renderedFormula"
      type="button"
      class="katex-preview"
      data-drag-handle
      :class="{ 'is-selected': selected }"
      @click="enterEditMode"
      @keydown.space="enterEditMode"
    ></button>
  </node-view-wrapper>
</template>

<style lang="scss" scoped>
.katex-wrapper {
  outline: transparent 1px solid;
  margin: 0.9em 0;
  transition: outline-color 0.2s ease;
  border-radius: $border-radius;

  &.selected {
    outline-color: $primary-bright;
  }
}

.katex-editor {
  display: flex;
  flex-direction: column;
  padding: $spacer-3;
  background-color: $gray-100;
  margin-bottom: $spacer-3;

  &__header {
    display: flex;
    margin-bottom: $spacer-2;
  }

  &__preview {
    background-color: $color-white;
    border: 1px solid $border-color;
    border-radius: $border-radius;
    padding: $spacer-3;
    margin-bottom: $spacer-3;
  }
}

.katex-preview {
  background-color: $gray-100;
  border: none;
  border-radius: $border-radius;
  padding: $spacer-3;

  &:hover {
    background-color: $brand-tools-light;
    cursor: pointer;
  }
}
</style>
